import { useMutation, useQuery } from 'react-query';
import { datadogLogs } from '@datadog/browser-logs';
import { IResult } from '../../interfaces/result.interface';
import { ICoordinateComments } from '../../interfaces/coordinate-comments.interface';
import { IBiodiversityUnit } from '../../interfaces/BNG-units.interface';
import { ISharedResults } from '../../interfaces/shared-results.interface';
import { baseApi, buildAuthHeaders, throwApiError, useRetrieveToken } from './shared';

export const useResultApi = () => {
  const { retrieveToken, isAuthenticated, getAccessTokenSilently } = useRetrieveToken();

  const createResult = useMutation(async (resultData: IResult) => {
    const token = await retrieveToken('createResult');
    if (!token) return;

    const response = await fetch(`${baseApi}/results`, {
      method: 'POST',
      headers: buildAuthHeaders(token),
      body: JSON.stringify(resultData),
    });

    if (!response.ok) await throwApiError(response, 'createResult');

    return response.json();
  });

  const updateResult = useMutation(async (resultData: IResult) => {
    const token = await retrieveToken('updateResult');
    if (!token) return;

    if (!resultData.project?._id) {
      throw new Error('Project ID is required to update result');
    }

    const response = await fetch(`${baseApi}/results/${resultData.project._id}/additional-notes`, {
      method: 'PATCH',
      headers: buildAuthHeaders(token),
      body: JSON.stringify({ additionalNotes: resultData.additionalNotes }),
    });

    if (!response.ok) await throwApiError(response, 'updateResult', { resultData });

    return response;
  });

  const publishResult = useMutation(async (resultData: IResult) => {
    const token = await retrieveToken('publishResult');
    if (!token) return;

    const response = await fetch(`${baseApi}/results/publish`, {
      method: 'POST',
      headers: buildAuthHeaders(token),
      body: JSON.stringify(resultData),
      cache: 'no-cache',
    });

    if (!response.ok) await throwApiError(response, 'publishResult');
  });

  const addResult = useMutation(async (projectId: string) => {
    const token = await retrieveToken('addResult');
    if (!token) return;

    const response = await fetch(`${baseApi}/results/shared/${projectId}`, {
      method: 'POST',
      headers: buildAuthHeaders(token),
      body: JSON.stringify({ projectId }),
    });

    if (!response.ok) {
      datadogLogs.logger.error('Failed to add shared result', {
        name: 'addResult',
        response: { status: response.status, statusText: response.statusText },
      });
      throw new Error('Failed to create product');
    }
  });

  const setResultsPermissions = useMutation(
    async ({ projectId, shareResultsArtifacts }: { projectId: string; shareResultsArtifacts: boolean }) => {
      const token = await retrieveToken('setResultsPermissions');
      if (!token) return;

      const response = await fetch(`${baseApi}/results/permissions/${projectId}/`, {
        method: 'POST',
        headers: buildAuthHeaders(token),
        body: JSON.stringify({ shareResultsArtifacts }),
      });

      if (!response.ok) {
        await throwApiError(response, 'setSharingPermissions', {
          payload: { projectId, shareResultsArtifacts },
        });
      }

      return true;
    },
  );

  const useResultByProjectId = (projectId: string) => {
    return useQuery(
      ['results', projectId],
      async ({ signal }) => {
        const token = await getAccessTokenSilently();

        const response = await fetch(`${baseApi}/results/project/${projectId}`, {
          method: 'GET',
          headers: buildAuthHeaders(token),
          signal,
          cache: 'no-cache',
        });

        if (!response?.ok) {
          datadogLogs.logger.error('Failed to fetch project results data', {
            name: 'useResultByProjectId',
            response: { status: response.status, statusText: response.statusText },
          });
          throw new Error('Failed to fetch project results data');
        }

        const responseJson = await response.json();
        return responseJson?.resultData as IResult;
      },
      { refetchOnWindowFocus: false, refetchOnMount: false, cacheTime: 15 * 60 * 1000 },
    );
  };

  const useSharedResults = (enabled = true) => {
    return useQuery<unknown, unknown, ISharedResults[], any>(
      ['shared-results'],
      async ({ signal }) => {
        const token = await retrieveToken('getSharedResults');
        if (!token) return;

        let response: Response;
        try {
          response = await fetch(`${baseApi}/results/shared/`, {
            method: 'GET',
            headers: buildAuthHeaders(token),
            signal,
          });
        } catch (error) {
          if ((error as Error).name === 'AbortError') return;
          throw error;
        }

        if (!response?.ok) {
          datadogLogs.logger.error('Failed to fetch shared results', {
            name: 'getSharedResults',
            response: { status: response?.status, statusText: response?.statusText },
          });
          throw new Error('Failed to fetch shared results');
        }

        return (await response?.json())?.resultData as ISharedResults[];
      },
      {
        cacheTime: 3600000,
        enabled: isAuthenticated && enabled,
        retry: 2,
        onError: (error) => console.warn(error),
        retryDelay: 5000,
      },
    );
  };

  const useCoordinateCommentsByResultId = (resultId: string, signal?: AbortSignal) => {
    return useQuery<unknown, unknown, ICoordinateComments[], any>(
      ['result_by_result_id', resultId],
      async () => {
        const token = await retrieveToken('getCoordinateCommentsByResultId');
        if (!token) return;

        const response = await fetch(`${baseApi}/results/${resultId}/coordinate-notes`, {
          method: 'GET',
          headers: buildAuthHeaders(token),
          signal,
          cache: 'no-cache',
        });

        if (!response?.ok) return undefined;

        const responseJson = await response.json();
        return (responseJson.resultData as ICoordinateComments[]) || [];
      },
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        cacheTime: 3600000,
        enabled: !!resultId && isAuthenticated,
      },
    );
  };

  const updateCoordinateComments = useMutation(
    async ({
      resultData,
      coordinateComments,
    }: {
      resultData: IResult;
      coordinateComments: ICoordinateComments;
    }) => {
      const token = await retrieveToken('updateCoordinateComments');
      if (!token) return;

      const response = await fetch(`${baseApi}/results/${resultData._id}/coordinate-notes`, {
        method: 'PATCH',
        headers: buildAuthHeaders(token),
        body: JSON.stringify(coordinateComments),
      });

      if (!response.ok) await throwApiError(response, 'updateCoordinateComments', { resultData });

      return response;
    },
  );

  const deleteCoordinateComment = useMutation(
    async ({ coordinateCommentsId }: { coordinateCommentsId: string }) => {
      const token = await retrieveToken('deleteCoordinateComment');
      if (!token) return;

      const response = await fetch(`${baseApi}/results/${coordinateCommentsId}/coordinate-notes`, {
        method: 'DELETE',
        headers: buildAuthHeaders(token),
      });

      if (!response.ok) {
        datadogLogs.logger.error('Failed to delete coordinate comment', {
          name: 'deleteCoordinateComment',
          response: { status: response.status, statusText: response.statusText },
        });
        throw new Error('Failed to delete coordinate comment');
      }
    },
  );

  const useBNGUnitsByProjectId = (projectId: string, signal?: AbortSignal) => {
    return useQuery<unknown, unknown, IBiodiversityUnit, any>(
      ['getBNGUnitsByProjectId', projectId],
      async () => {
        const token = await retrieveToken('getBNGUnitsByProjectId');
        if (!token) return;

        const response = await fetch(`${baseApi}/results/${projectId}/units`, {
          method: 'GET',
          headers: buildAuthHeaders(token),
          signal,
          cache: 'no-cache',
        });

        if (!response?.ok) return undefined;

        const responseJson = await response.json();
        return responseJson.resultData as IBiodiversityUnit;
      },
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        cacheTime: 3600000,
        enabled: !!projectId && isAuthenticated,
      },
    );
  };

  return {
    createResult,
    updateResult,
    publishResult,
    addResult,
    setResultsPermissions,
    useResultByProjectId,
    useSharedResults,
    useCoordinateCommentsByResultId,
    updateCoordinateComments,
    deleteCoordinateComment,
    useBNGUnitsByProjectId,
  };
};
